import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

import AdminContainer from "../components/AdminContainer";
import ordersService from "../api/services/orders";
import { priceFormatter } from "../utils/utils";
import RevenueChart from "../components/admin/RevenueChart";

const DashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    ordersService
      .getOrderStats()
      .then((data) => {
        setStats(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  return (
    <AdminContainer>
      <Top>
        <div>
          <Title>Dashboard</Title>
          <Sub>Pregled za poslednjih 30 dana</Sub>
        </div>
      </Top>

      {loading ? (
        <Info>Učitavanje statistike…</Info>
      ) : error ? (
        <Info>Greška: {error}</Info>
      ) : (
        <>
          <Kpis>
            <Kpi data-kj="収">
              <KpiLabel>Zarada ovog meseca</KpiLabel>
              <KpiValue>
                {priceFormatter(stats.thisMonthRevenue ?? 0)}{" "}
                <small>RSD</small>
              </KpiValue>
            </Kpi>
            <Kpi data-kj="旧">
              <KpiLabel>Zarada prošlog meseca</KpiLabel>
              <KpiValue>
                {priceFormatter(stats.lastMonthRevenue ?? 0)}{" "}
                <small>RSD</small>
              </KpiValue>
            </Kpi>
            <Kpi data-kj="全">
              <KpiLabel>Ukupna zarada</KpiLabel>
              <KpiValue>
                {priceFormatter(stats.totalRevenue ?? 0)} <small>RSD</small>
              </KpiValue>
            </Kpi>
            <Kpi data-kj="注">
              <KpiLabel>Porudžbine (30d)</KpiLabel>
              <KpiValue>{stats.ordersCount}</KpiValue>
            </Kpi>
            <Kpi data-kj="平">
              <KpiLabel>Prosečna korpa</KpiLabel>
              <KpiValue>
                {priceFormatter(stats.avgOrder)} <small>RSD</small>
              </KpiValue>
            </Kpi>
            <Kpi data-kj="新" $alert={stats.pendingCount > 0}>
              <KpiLabel>Nove porudžbine</KpiLabel>
              <KpiValue $red={stats.pendingCount > 0}>
                {stats.pendingCount}
              </KpiValue>
            </Kpi>
          </Kpis>

          <Panel>
            <PanelTitle>
              Prihod po danima
              <PanelLink
                onClick={() => navigate("/kontrolna-tabla/porudzbine")}
              >
                Sve porudžbine →
              </PanelLink>
            </PanelTitle>

            {stats.perDay?.length ? (
              <RevenueChart perDay={stats.perDay} />
            ) : (
              <Info>Nema podataka za grafikon.</Info>
            )}
          </Panel>

          <Panel>
            <PanelTitle $warn>
              ⚠ Rasprodati proizvodi ({stats.soldOutProducts?.length || 0})
            </PanelTitle>

            {stats.soldOutProducts?.length ? (
              <SoldOutList>
                {stats.soldOutProducts.map((p) => (
                  <SoldOutItem
                    key={p.id}
                    onClick={() =>
                      navigate(`/kontrolna-tabla/edit/${p.id}`)
                    }
                  >
                    <span>{p.name}</span>
                    <Pill>Rasprodato</Pill>
                  </SoldOutItem>
                ))}
              </SoldOutList>
            ) : (
              <Info>Sve je na stanju. 改善</Info>
            )}
          </Panel>
        </>
      )}
    </AdminContainer>
  );
};

export default DashboardPage;

const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 2.4rem;
  flex-wrap: wrap;
  gap: 1.4rem;
`;

const Title = styled.h1`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  font-size: 3rem;
  letter-spacing: 0.02em;
  color: var(--text, #f4f2ee);
`;

const Sub = styled.div`
  color: var(--muted, #8e8c94);
  font-size: 1.3rem;
  font-family: "Poppins-Regular";
  margin-top: 0.4rem;
`;

const Kpis = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(21rem, 1fr));
  gap: 1.4rem;
  width: 100%;
  margin-bottom: 2.2rem;
`;

const Kpi = styled.div`
  position: relative;
  overflow: hidden;
  background: var(--bg2, #17171c);
  border: 1px solid
    ${(props) =>
      props.$alert ? "rgba(254, 95, 85, 0.35)" : "var(--line, rgba(255,255,255,0.07))"};
  padding: 2.2rem;
  border-radius: 0.6rem;

  &::after {
    content: attr(data-kj);
    position: absolute;
    right: 0.6rem;
    bottom: -1.6rem;
    font-family: var(--font-kanji);
    font-size: 6.4rem;
    color: rgba(255, 255, 255, 0.04);
    pointer-events: none;
  }
`;

const KpiLabel = styled.div`
  font-size: 1.1rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--muted, #8e8c94);
  font-family: "Poppins-Medium";
`;

const KpiValue = styled.div`
  font-family: "Anton", sans-serif;
  font-size: clamp(2.4rem, 5vw, 3.2rem);
  margin-top: 0.8rem;
  line-height: 1;
  color: ${(props) => (props.$red ? "var(--red, #fe5f55)" : "var(--text, #f4f2ee)")};

  small {
    font-size: 1.4rem;
    color: var(--muted, #8e8c94);
    font-family: "Poppins-Medium";
  }
`;

const Panel = styled.div`
  background: var(--bg2, #17171c);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-radius: 0.6rem;
  padding: 2.4rem;
  width: 100%;
  margin-bottom: 2.2rem;

  @media (max-width: 640px) {
    padding: 1.6rem 1.2rem;
  }
`;

const PanelTitle = styled.h3`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.3rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${(props) => (props.$warn ? "var(--red, #fe5f55)" : "var(--muted, #8e8c94)")};
  font-family: "Poppins-Medium";
  margin-bottom: 1.8rem;
`;

const PanelLink = styled.span`
  font-size: 1.1rem;
  color: var(--red, #fe5f55);
  cursor: pointer;
  letter-spacing: 0.1em;

  &:hover {
    text-decoration: underline;
  }
`;


const SoldOutList = styled.ul`
  list-style: none;
  width: 100%;
`;

const SoldOutItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.1rem 0;
  border-bottom: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  font-size: 1.3rem;
  font-family: "Poppins-Medium";
  color: var(--text, #f4f2ee);
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover span {
    color: var(--red, #fe5f55);
  }
`;

const Pill = styled.span`
  font-size: 1rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-family: "Poppins-SemiBold";
  padding: 0.4rem 1rem;
  border-radius: 10rem;
  background: rgba(255, 255, 255, 0.06);
  color: var(--muted, #8e8c94);
`;

const Info = styled.div`
  color: var(--muted, #8e8c94);
  font-size: 1.4rem;
  font-family: "Poppins-Medium";
  padding: 2rem 0;
  width: 100%;
  text-align: center;
`;
